import React, { useState } from 'react'
import { Alert, Row, Col, Table, Tag } from 'antd'
import { appSelector } from '../../helpers/appSelector'
import Filters from './Filters'
import moment from 'moment'
import { roles } from '../../helpers/constants'
import { MerchantData, FundTransaction as TT } from "../../interfaces";
import { isEmpty } from '../../helpers/isEmpty'
import { clearStates, reportFundTransactionsRequest } from '../../store/allocate-funds'
import { AppDispatch } from '../../helpers/appDispatch'
import { useDispatch } from 'react-redux'
import Accounts from '../../pages/_accounts'
import { clearMerchantVASAccountBalance, getMerchantVASAccountBalance } from '../../store/home'
import { formatUtcToUserTime } from '../../utils/dateTime'

interface Props {
  translate: any
  setOpenModal: () => void
  onClickRow(transactionID: number): void
}

const AllocateFundsDashboard: React.FC<Props> = (props) => {
  const dispatch: AppDispatch = useDispatch()
  const { translate, setOpenModal, onClickRow } = props
  const {
    reportTransactions,
    isFetchingReportTransactions,
    reportTransactionsError,
    fundTransactionsExportError
  } = appSelector((state) => state.allocateFundState);
  const { user } = appSelector((state) => state.auth)
  const reports = appSelector((state) => state.reports)
  const home = appSelector((state) => state.home)
  const { merchants } = reports
  const [merchant, setMerchant] = useState<MerchantData | null>(null)
  const [periodFrom, setPeriodFrom] = useState('')
  const [periodTo, setPeriodTo] = useState('')
  const { loadingAccounts, merchantAccounts } = home

  const getErrorMessage = (error: any) => {
    if (typeof error === 'string') return error
    return error?.detail || error?.message || error?.title || JSON.stringify(error)
  }

  let role: any;
  if (user) {
    role = user.roles.find((r) => r === roles.SuperMerchant);
  } else {
    role = roles.SuperMerchant;
  }
  
  const payload = {
    periodFrom: periodFrom,
    periodTo: periodTo,
    merchant: merchant ? merchant?.userId : 0,
  }

  const onSearch = (values: any) => {
    const { merchant, periodFrom, periodTo } = values
    let pFrom: string = '',
      pTo: string = ''
    let m: MerchantData | undefined = undefined
  
    if (!isEmpty(periodFrom) && !isEmpty(periodTo)) {
      pFrom = moment(periodFrom).format('MM/DD/YYYY')
      pTo = moment(periodTo).format('MM/DD/YYYY')
      setPeriodFrom(pFrom)
      setPeriodTo(pTo)
    }
    if (!isEmpty(merchant)) {
      const selectedMerchantId = Number(merchant)
      const merchantList = !isEmpty(merchants) ? merchants : reports.merchants
      m = merchantList.find((merchant) => merchant.merchantId === selectedMerchantId)
      if (m !== undefined) {
        setMerchant(m)

        dispatch(getMerchantVASAccountBalance(m.merchantId))
      } else {
        setMerchant(null)
        dispatch(clearMerchantVASAccountBalance())
      }
    } else {
      setMerchant(null)
      dispatch(clearMerchantVASAccountBalance())
    }
    payload.periodFrom = pFrom
    payload.periodTo = pTo
    payload.merchant = m !== undefined ? m?.userId : 0
    dispatch(clearStates())
    dispatch(reportFundTransactionsRequest(payload))
  }
  
  const onReset = (form: any) => {
    form.resetFields()
    payload.periodFrom = ''
    payload.periodTo = ''
    payload.merchant = 0
    setMerchant(null)
    setPeriodFrom('')
    setPeriodTo('')
    dispatch(clearMerchantVASAccountBalance())
    dispatch(reportFundTransactionsRequest(payload))
  }

  const columns: any = [
    {
      title: <span style={{ fontSize: "2rem", color: "#868686" }}>&bull;</span>,
      dataIndex: "status",
      key: "status",
      align: "center",
      className: "column-text",
      render: (status: string) => {
        return <span style={{ fontSize: "2rem", color: "#868686" }}>&bull;</span>;
      },
    },
    {
      title: `${translate("general.amount")}`,
      dataIndex: "amount",
      key: "amount",
      align: "center",
      className: "column-text",
    },
    {
      title: `${translate("general.debitAccount")}`,
      dataIndex: "debitAccount",
      key: "debitAccount",
      align: "center",
      className: "column-text",
    },
    {
      title: `${translate("general.creditAccount")}`,
      dataIndex: "creditAccount",
      key: "creditAccount",
      align: "center",
      className: "column-text",
    },
    {
      title: `${translate("general.createdAt")}`,
      dataIndex: "trxDate",
      key: "trxdate",
      align: "center",
      className: "column-text",
      responsive: ["lg"],
      render: (date: string) => {
        const d = formatUtcToUserTime(date);
        return <span>{d}</span>;
      },
    },
    {
      title: `${translate("general.status")}`,
      dataIndex: "status",
      key: "status",
      align: "center",
      className: "column-text",
      responsive: ["lg"]
    },
    {
      title: `${translate("general.approvalStatus")}`,
      dataIndex: "approvalStatusId",
      key: "approvalStatusId",
      align: "center",
      className: "column-text",
      responsive: ["lg"],
      render: (approvalStatusId: number) => {
        switch (approvalStatusId) {
          case 1:
            return (
              <Tag key={approvalStatusId}  style={{ color: '#35b9e6', backgroundColor:'transparent', border:'1px solid #35b9e6', borderRadius: '10px' }}>
                {'Submitted to Checker'}
              </Tag>
            );
          case 2:
            return (
              <Tag key={approvalStatusId} className="table-tag-merchant">
                {'Approved'}
              </Tag>
            );
          case 3:
            return (
              <Tag key={approvalStatusId}  style={{ color: 'red', backgroundColor:'transparent', border:'1px solid red', borderRadius: '10px' }}>
                {'Rejected'}
              </Tag>
            );
          default:
            break;
        }
        
      },
    }
  ];

  let dataSource: TT[] = [];
  for (let transaction of reportTransactions) {
    dataSource.push({
      amount: `${transaction.currency} ${transaction.amount}`,
      transactionId: transaction.transactionId,
      trxDate: transaction.trxDate,
      status: transaction.status,
      debitAccount: transaction.debitAccount,
      creditAccount: transaction.creditAccount,
      currency: transaction.currency,
      approvalStatusId: transaction.approvalStatusId,
      approvalStatus: transaction.approvalStatus
    });
  }

  return (
    <Row gutter={20}>
      <Col span={24}>
        <div className="table-padding ">
          {role !== undefined && role === roles.SuperMerchant && merchant !== undefined && merchant !== null ? (
            <div style={{ display: "flex", justifyContent:"flex-end" }}>
              <span className="align-self-center" style={{ fontSize:"14px", marginRight: "5px"}}>
                {`${merchant ? merchant.name : ''} ${translate("general.balance")}:`}
              </span>
              <Accounts
                accounts={merchantAccounts}
                loading={loadingAccounts}
              />
            </div>
          ): null}
          <Filters
            merchants={merchants}
            onSearch={onSearch}
            onReset={onReset}
            translate={translate}
            setOpenModal={setOpenModal}
          />
          {reportTransactionsError && (
            <Alert
              message="Failed to fetch data"
              description={getErrorMessage(reportTransactionsError)}
              type="error"
              showIcon
              style={{ marginTop: 10 }}
            />
          )}
          {fundTransactionsExportError && (
            <Alert
              message="Failed to export data"
              description={getErrorMessage(fundTransactionsExportError)}
              type="error"
              showIcon
              style={{ marginTop: 10 }}
            />
          )}
        </div>
        <div className="table-padding">
          <Table
            dataSource={dataSource}
            columns={columns}
            bordered
            className="tranaction-table"
            onRow={(t: TT) => ({
              onClick: () => onClickRow(t.transactionId),
            })}
            pagination={{
              total: reportTransactions.length,
              showTotal: (_, range) => {
                const tran = translate(`general.pagination`)
                let t = tran.replace(`%d`, `${range[0]} - ${range[1]}`)
                let s = t.replace(`%s`, reportTransactions.length)
                return s
              },
              showSizeChanger: false
            }}
            loading={isFetchingReportTransactions}
            />
          </div>
      </Col>
    </Row>
  )
}

export default AllocateFundsDashboard
